/**
 * Velrey Motion — GSAP lifecycle manager for Shopify sections.
 *
 * Shopify's theme editor injects and removes section markup at runtime, so any
 * animation created for a section has to be torn down when that section goes
 * away. This module is the Liquid equivalent of a React `useEffect` cleanup:
 * every section registers an init function, we run it inside a `gsap.context()`,
 * and we revert that context on `shopify:section:unload`.
 *
 * Usage from a section's <script>:
 *
 *   VelreyMotion.register('hero', (root, ctx) => {
 *     gsap.from(root.querySelectorAll('[data-reveal]'), { y: 40, opacity: 0, stagger: 0.08 });
 *   });
 *
 * The section's wrapper element must carry `data-velrey-section="hero"`.
 */
window.VelreyMotion = (function () {
  const registry = new Map();
  const contexts = new WeakMap();

  // Whatever the head stub collected while the page was still parsing.
  const queued = (window.VelreyMotion && window.VelreyMotion._q) || [];

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined') {
    window.gsap.registerPlugin(window.ScrollTrigger);
  }

  function ready() {
    return typeof window.gsap !== 'undefined';
  }

  /** Register the animation for a section type. Idempotent per type. */
  function register(type, initFn) {
    registry.set(type, initFn);
    // A section of this type may already be in the DOM (script runs after markup).
    document
      .querySelectorAll(`[data-velrey-section="${type}"]`)
      .forEach(mount);
  }

  /** Create the gsap.context for one section root. */
  function mount(root) {
    if (!ready() || contexts.has(root)) return;

    const type = root.dataset.velreySection;
    const initFn = registry.get(type);
    if (!initFn) return;

    // Reduced motion: reveal everything immediately, skip the timeline entirely.
    if (reducedMotion.matches) {
      root.querySelectorAll('[data-reveal]').forEach((el) => {
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
      contexts.set(root, null);
      return;
    }

    const ctx = window.gsap.context((self) => initFn(root, self), root);
    contexts.set(root, ctx);
  }

  /** Revert the context so GSAP undoes every inline style it wrote. */
  function unmount(root) {
    const ctx = contexts.get(root);
    if (ctx) ctx.revert();
    contexts.delete(root);
  }

  function mountAll() {
    document.querySelectorAll('[data-velrey-section]').forEach(mount);
  }

  /**
   * ScrollTrigger measures positions from the layout at creation time. Images
   * that finish decoding afterwards shift everything below them, so pins and
   * scrub ranges end up pointing at stale offsets. Recalculate once the page
   * has fully settled.
   */
  function refreshOnLoad() {
    if (typeof window.ScrollTrigger === 'undefined') return;
    window.addEventListener('load', () => window.ScrollTrigger.refresh());
  }

  // Drain the stub's queue. Deferred scripts run before DOMContentLoaded, so
  // by the time the listener below fires every section is already registered.
  queued.forEach(([type, initFn]) => register(type, initFn));

  document.addEventListener('DOMContentLoaded', () => {
    mountAll();
    refreshOnLoad();
  });

  // Theme editor lifecycle.
  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll('[data-velrey-section]').forEach(mount);
    if (e.target.matches?.('[data-velrey-section]')) mount(e.target);
    window.ScrollTrigger?.refresh();
  });

  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-velrey-section]').forEach(unmount);
    if (e.target.matches?.('[data-velrey-section]')) unmount(e.target);
    window.ScrollTrigger?.refresh();
  });

  // Selecting a block in the editor scrolls the preview; pinned sections need
  // their measurements back in sync afterwards.
  document.addEventListener('shopify:block:select', () => window.ScrollTrigger?.refresh());

  return { register, mount, unmount, mountAll, get reducedMotion() { return reducedMotion.matches; } };
})();
